import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Minus, Plus, History, Trash2, Printer } from "lucide-react";
import { printTrenitaliaTicket } from "@/components/admin/printTrenitaliaTicket";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const MovesList = ({ moves }) => (
  <div className="mt-3 max-h-48 overflow-y-auto text-sm divide-y" data-testid="trenitalia-moves">
    {moves.length === 0 && <p className="text-slate-400 py-2">Aucun mouvement</p>}
    {moves.map((m) => (
      <div key={m.id} className="flex justify-between py-1.5">
        <span className="text-slate-500">{new Date(m.created_at).toLocaleString("fr-FR")}{m.note ? ` — ${m.note}` : ""}</span>
        <span className={m.delta > 0 ? "text-green-600 font-medium" : "text-red-600 font-medium"}>
          {m.delta > 0 ? "+" : ""}{m.delta} pts <span className="text-slate-400">({m.balance})</span>
        </span>
      </div>
    ))}
  </div>
);

export const TrenitaliaClientCard = ({ client, headers, onChange }) => {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [moves, setMoves] = useState(null);

  const loadMoves = async () => {
    const res = await axios.get(`${API}/admin/trenitalia/clients/${client.id}/moves`, { headers });
    setMoves(res.data);
  };

  const move = async (sign) => {
    const value = parseInt(amount, 10);
    if (!value || value <= 0) { toast.error("Entrez un nombre de points"); return; }
    try {
      await axios.post(`${API}/admin/trenitalia/clients/${client.id}/moves`, { delta: sign * value, note: note || null }, { headers });
      toast.success(sign > 0 ? `+${value} pts ajoutés` : `-${value} pts débités`);
      setAmount(""); setNote("");
      onChange();
      if (moves) loadMoves();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const print = async () => {
    const res = await axios.get(`${API}/admin/trenitalia/clients/${client.id}/moves`, { headers });
    printTrenitaliaTicket(client, res.data);
  };

  const remove = async () => {
    if (!window.confirm(`Supprimer ${client.first_name} ${client.last_name} et son historique ?`)) return;
    await axios.delete(`${API}/admin/trenitalia/clients/${client.id}`, { headers });
    toast.success("Client supprimé");
    onChange();
  };

  return (
    <div className="bg-white rounded-xl border p-4" data-testid={`trenitalia-client-${client.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="font-semibold">{client.last_name.toUpperCase()} {client.first_name}</p>
          <p className="text-2xl font-bold text-emerald-600" data-testid={`trenitalia-balance-${client.id}`}>{client.points} pts</p>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={print} title="Imprimer" data-testid={`trenitalia-print-btn-${client.id}`}><Printer className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" onClick={() => (moves ? setMoves(null) : loadMoves())} title="Historique" data-testid={`trenitalia-history-btn-${client.id}`}><History className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" className="text-red-500" onClick={remove} title="Supprimer" data-testid={`trenitalia-delete-btn-${client.id}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-[90px_1fr] gap-2">
        <Input type="number" min="1" placeholder="Pts" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid={`trenitalia-amount-${client.id}`} />
        <Input placeholder="Note (optionnel)" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`trenitalia-note-${client.id}`} />
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <Button className="bg-green-600 hover:bg-green-700" onClick={() => move(1)} data-testid={`trenitalia-credit-btn-${client.id}`}><Plus className="w-4 h-4 mr-1" />Ajouter</Button>
        <Button variant="outline" className="text-red-600 border-red-300" onClick={() => move(-1)} data-testid={`trenitalia-debit-btn-${client.id}`}><Minus className="w-4 h-4 mr-1" />Débiter</Button>
      </div>
      {moves && <MovesList moves={moves} />}
    </div>
  );
};
